/**
 * Tokyo Metro — client-side train phase resolver.
 *
 * The server delegates actual train movement to the client (simpler
 * than building graph logic in PL/pgSQL). The client computes the
 * new train positions + any station payouts that fire along the way
 * and commits them atomically via `tm_commit_train_phase`.
 *
 * Rules (simplified for v1):
 *   - Running trains move 5 + speedTokens spaces along their route.
 *   - At each route step, check if the current station has an owner.
 *     If so, trigger a payout (200 yen + line income +200 if owner has
 *     no stock in the line; line income +500 if owner owns stock).
 *   - Trains reverse at the terminus (routeIndex reaches either end).
 */
import { LINE_ROUTES, type LineId } from '../data/map'
import type { TMRoomState } from '../data/types'

export interface PayoutPatch {
  playerId: string
  yenDelta: number
}

export interface TrainPhaseResult {
  trains: TMRoomState['trains']
  payouts: PayoutPatch[]
}

export function resolveTrainPhase(state: TMRoomState): TrainPhaseResult {
  const payouts = new Map<string, number>()
  const addYen = (playerId: string, delta: number) => {
    payouts.set(playerId, (payouts.get(playerId) ?? 0) + delta)
  }

  // Clone trains so the caller has fresh references for commit.
  const trains = state.trains.map(t => ({ ...t }))
  const stations = state.stations

  for (const train of trains) {
    if (!train.running) continue
    const route = LINE_ROUTES[train.line as LineId]
    if (!route || route.length === 0) continue

    const steps = 5 + (train.speedTokens ?? 0)
    let idx = train.routeIndex
    let dir: 1 | -1 = train.direction

    for (let step = 0; step < steps; step++) {
      idx += dir
      // Bounce at terminus.
      if (idx < 0) { idx = 1; dir = 1 }
      else if (idx >= route.length) { idx = route.length - 2; dir = -1 }

      const stationId = route[idx]
      const st = stations.find(s => s.stationId === stationId)
      if (!st?.ownerId) continue

      // Does the owner have stock in this line?
      //   - stations don't carry enough info to answer that cheaply;
      //     v1 uses the simpler rule: pyramid owner gets 200 yen every
      //     time a train passes through. Income gets a +200 bump
      //     regardless. Stock-aware payouts land in v2.
      addYen(st.ownerId, 200)
      train.income += 200
    }

    train.routeIndex = idx
    train.direction = dir
  }

  return {
    trains,
    payouts: [...payouts.entries()].map(([playerId, yenDelta]) => ({
      playerId, yenDelta,
    })),
  }
}
